/* eslint-disable sort-keys */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocalizationContext } from '@im/react-localization';
import { useBuildVars, useInX4AFrame, useSiteConfig } from 'mf_core/config';
import { injectScriptInHeadWith } from './utils';

export function useOneTrust() {
  const { buildEnvironment } = useBuildVars();
  const { siteCode } = useSiteConfig();
  const { locale } = useLocalizationContext();
  const { isInIM360Frame, isInX4AFrame } = useInX4AFrame();
  const isScriptInjected = useRef(false);
  const win = window as any;

  //Updates Script in Head and needs to be synchronous
  useLayoutEffect(() => {
    //When Opening X4C/X4A inside an iFrame in CRM/IM360 OneTrust SDK is unable to read the cookies stored in
    //the localstorage since CRM domain and X4C domain are different and browser is not allowing access.
    //Added This Effect to prevent injecting OneTrust Scripts in CRM/IM360 iFrame screen againt BUG ADT-15210
    if (!isScriptInjected.current && !isInIM360Frame && !isInX4AFrame) {
      let scriptId = '';
      if (buildEnvironment && siteCode) {
        const envMap = scriptsMap[buildEnvironment];
        if (envMap) {
          scriptId = envMap[siteCode];
        }
      }
      if (!isScriptInjected.current && scriptId) {
        loadOneClickCSS();
        injectScriptInHeadWith(
          `https://cdn.cookielaw.org/consent/${scriptId}/OtAutoBlock.js`
        );
        injectScriptInHeadWith(
          'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
          undefined,
          scriptId
        );
        injectScriptInHeadWith(
          undefined,
          `function OptanonWrapper() { window.dispatchEvent(new Event('bannerLoaded')); }`
        );
        isScriptInjected.current = true;
        if (window as any) (window as any).isOneTrustEnabled = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildEnvironment, siteCode]);

  useEffect(() => {
    if (isScriptInjected.current && win?.OneTrust?.changeLanguage) {
      win.OneTrust?.changeLanguage(locale.slice(0, 2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);
}

/** Return `loading` at first and then sends script id string or `empty` string once ready*/
export function useIsOneTrustEnabled() {
  const { buildEnvironment } = useBuildVars();
  const { siteCode } = useSiteConfig();
  const [hasId, setHasId] = useState<string | undefined>('loading');

  useEffect(() => {
    let scriptId = '';
    if (buildEnvironment && siteCode) {
      const envMap = scriptsMap[buildEnvironment];
      if (envMap) {
        scriptId = envMap[siteCode];
      }
    }
    if (scriptId) {
      setHasId(scriptId);
    } else {
      setHasId('');
    }
  }, [buildEnvironment, siteCode]);

  return hasId;
}

const scriptsMap: any = {
  development: {
    de: '697b043e-f485-43da-a6a6-2734a4ec8b88-test',
    us: '79356f9e-23dd-4222-b2de-b0c37f4d5a5b-test',
  },
  production: {
    at: '44dd8255-aa4b-4557-b80b-2a24ea659e53',
    au: 'bf88a6d4-2148-404e-8c96-e5dd58f4805d',
    be: '0b0037df-0851-4896-aa89-9d19ed8890dd',
    br: '0565517d-8776-4bde-bb6e-d575cebdb4d2',
    ca: '44ec1e95-68da-44a7-863e-82f4106d645e',
    ch: 'fd420875-1423-4cee-b31c-029a1e120ca5',
    cl: '4729ebea-73db-4cfe-b7e3-088a5c758694',
    co: 'd7f6df39-f565-4244-8afa-55852a43265d',
    de: 'fa481d81-76c4-45d3-9136-c5482c0363ae',
    es: 'aca353d8-ce1d-42b9-a0d8-013115fab766',
    fr: 'e355c5ab-f65e-43ae-b5c9-63ef3090fa1e',
    gb: '6b727968-81a4-44e2-bcf4-c223368323ae',
    hk: '5ba4d092-59de-4ff9-bd08-9a4b85ad9925',
    id: '88d87685-64de-4492-93d7-d4c0e5a41ec5',
    in: '0537784a-5bde-4b36-a534-68183186040c',
    it: '057e1217-f062-4d5c-a4c7-13a14dabaaac',
    mx: 'b4449d97-8e60-4e5d-8955-4cceec4ad8df',
    my: '82984dfd-1131-4286-9d56-b1ff935c20e7',
    nl: '946f4467-f8b2-497d-aeba-55bf5e5484be',
    nz: '4bc45039-3f8f-48e5-8e27-b67f08a7456e',
    pe: '550a2f66-7655-4c11-b251-338050840121',
    sg: 'b412ece9-d419-4990-a25c-e052c6fe5c70',
    us: '99ef7803-f71a-4a5a-ab1e-21f585d4b3de',
    usme: '164deb5f-2b4a-4d8e-83f6-609e2ed95978',
  },
  productionbeta: {
    at: 'a0b222df-4519-4a6d-8719-bb2f8822e74b',
    au: '15eac479-bc68-4963-8ac6-ee98bb301024',
    be: 'bba8f146-e67f-4903-8b80-717590ab150b',
    br: '4a93a70b-697f-4f0a-8610-cf65263820a4',
    ca: 'f7e521f6-1665-4f7d-9447-7bc6bf7c16af',
    ch: '300e9251-18a8-4b83-9ba5-7b84ca09b879',
    cl: '9a22cd8b-f862-4f8b-b7d1-24cfa6e6f6f1',
    cn: '0f1d96db-5406-4f27-9f4d-9069f62fe734',
    co: '5fabb3de-ea9b-4052-b476-3b4665473525',
    de: '05e95e92-9c0a-4ae3-b52d-aef50bc0b84b',
    dk: 'bb774640-b08b-4c41-a796-a30b0bcc0cb4',
    es: 'd1ccb41c-3ac8-4746-bdac-0f0b090b0b51',
    fi: '650d3abf-8138-45de-86bf-6efb2a8e9a45',
    fr: '1e116878-c4fb-4cbf-b992-777419629dc8',
    gb: 'ea831300-7839-414d-82df-8f96a0e2db91',
    hk: 'd504548e-4204-4c39-8e31-289a3590dfc0',
    hu: '2d1cb494-1ecf-4a46-a0ac-f9130fc0a1d8',
    id: '084e9439-8500-43e1-aa93-a8ecfa9f5e34',
    in: 'cc6cd377-80f0-4877-859c-924c2f5c5011',
    it: 'b2556e1c-ff2d-4fbb-bcea-887fdca1c4b6',
    mx: 'b0144b6a-7506-4cb7-8b7f-f6b3551b06a7',
    my: 'a235d6d9-b775-4e3f-94a1-f21877b1359c',
    nl: '3ae234b2-a8bc-4b40-88b6-308c7762b6e3',
    nz: 'a1c46dd5-17c7-4c18-8657-6e065eba6f31',
    pe: 'f364691f-43a1-4d3a-8cce-9b02e76b48e6',
    pt: '5f349b16-a1a4-430a-be83-54d90897448f',
    se: '10793762-afe5-4b3b-8455-7ed5cae4c349',
    sg: '85cd5b2c-f678-41ce-8ca0-8f000c19bc00',
    th: '33bed5fb-a581-4706-96c1-b58d93648747',
    us: 'a13a040c-af96-4a32-a274-68a0216bbfdf',
    usme: '4506acef-75b5-47d3-9e73-8f2f6182a70a',
  },
  productionbetax: {
    at: '01971bca-ef03-7013-8f7a-f35085daea21',
    au: '01971bf1-0181-7606-9069-8fc33d671be1',
    be: '01971bf3-95c0-736b-99e8-f8da48d75085',
    br: '01971bd5-1313-7561-8370-624aac5fc19a',
    ca: '01971bd8-ad28-7d43-a618-5eb01062afba',
    ch: '01971bd9-6dfe-760b-b8aa-a932998f110a',
    cl: '01971bda-a204-705c-b0e9-72e9538d7d42',
    cn: '01971bda-0ec0-7f39-9160-caa6d87e543d',
    co: '01971bdb-54c7-7d6d-85a1-af38007abd06',
    de: '01971bf4-1010-79f7-b414-2f2f3e31a06b',
    dk: '01971bf5-1617-7b11-aa7f-1b40f8b1704a',
    es: '01971bdf-c42a-76b0-b4da-30decc7f8e2e',
    fi: '01971bf5-905f-7333-84fa-15f2fd8958eb',
    fr: '01971be7-f7e6-7d74-ac29-17ffc59ef7ed',
    gb: '01971bee-d29d-7177-b6b4-a7a5c448e70f',
    hk: '01971be4-c05a-7570-8a13-92bf02642b28',
    hu: '01971bf1-ab69-75d9-977a-41bb307ee068',
    id: '01971bf0-85b3-78c1-9891-1d8ab0969dab',
    in: '01971bf6-1b92-7dca-aa62-d13a5987970a',
    it: '01971bf2-a51f-7003-95e9-d4d16fea377f',
    mx: '01971be8-74c9-7232-a794-a7666bb3f1c3',
    my: '01971be9-a053-73b4-b387-406be8a586e0',
    nl: '01971bf3-2129-7bf7-b014-fbf330c2bf66',
    nz: '01971beb-c96b-706e-95c9-1380004f2a01',
    pe: '01971bec-b442-7d40-8d84-7ec24a33b0d5',
    pt: '01971bed-339b-7f61-997b-ecf76b4af515',
    se: '01971bed-ab3b-74bc-a1a8-078fa288cfb2',
    sg: '01971bee-45d6-761f-94c3-f0ba1473d10c',
    th: 'f696c8c0-f434-4434-8a53-9da1d0898809',
    us: '01971bef-f349-7add-ae14-7c12faa597ff',
    usme: '63e9ac78-9fcc-4fc9-b0e0-3d6db513ff68',
  },
  staging: {
    sg: '0190e350-3e9e-76ef-9473-2f647ba9adac',
    hk: '0190e352-9455-7378-ad82-f58e6f44c1b6',
    id: '0190e35e-96d4-7ca2-b254-e56f662ac84c',
    nz: '0190e366-fbec-776f-8cb2-08a4fc55e311',
    br: '0190e36a-7658-7ffa-89b5-8fd8d2a5958e',
    mi: '0190e376-b8d2-79ce-86ad-b5c0d5d25bdf',
    pe: '0190e378-9c5c-7d78-8ae7-38240c480bbb',
    cl: '0190e37a-760c-79d5-aa1a-269932e73b42',
    co: '0190e37d-1ff3-7172-8592-7b79c4ff929b',
    at: '63148719-cda6-491f-9737-d7a53edc5d42',
    nl: '1a5c451e-b7a2-4439-84cd-acaf579c9149',
    be: '8c070da0-9422-440c-94b3-6bb589844324',
    ch: '0190e39c-705a-7fb8-a00f-69ae5ba3ccac',
    au: 'f31ce79d-fdd9-491f-bdf5-2539c34a1fc5',
    ca: 'f0fa73fd-2424-40b3-8b9d-b6d0795a71ea',
    cn: '68c9a61c-5582-495f-b447-9518c6a0c5bf',
    de: '697b043e-f485-43da-a6a6-2734a4ec8b88',
    dk: '1dc736f6-747e-489a-b63d-99c3d579ae0d',
    es: 'da79cfbb-7673-4761-b5eb-c9fcb6a8f1fd',
    fi: '58c78875-00a1-468d-b690-2d7feea0df81',
    fr: '826d79e4-068f-4dbf-a5d0-25a2ca7a8316',
    gb: '009a5a44-7658-4e58-b93c-3920cfe31961',
    hu: '3cdd9702-b7c9-4078-8564-a975dab79ac3',
    in: '337ba5fc-3a4c-4d14-94b5-23235ce52259',
    it: '959211c2-d26d-4248-9be6-3653b636c5d9',
    mx: '62854f5a-0804-4a50-9b06-e57c424d5d04',
    my: '5fe7808e-9d34-4b79-a522-cf404f882753',
    pt: '0258463a-9827-4ae3-b982-d5c5108c1fbd',
    se: 'ccd5f5d6-dd0a-47da-98f0-06a2800f5199',
    th: '8d30ab41-5f11-426e-9382-d5bd1a5729af',
    us: '79356f9e-23dd-4222-b2de-b0c37f4d5a5b',
    usme: '0190e376-b8d2-79ce-86ad-b5c0d5d25bdf',
  },
  stagingx: {
    sg: '0190e350-3e9e-76ef-9473-2f647ba9adac',
    hk: '0190e352-9455-7378-ad82-f58e6f44c1b6',
    id: '0190e35e-96d4-7ca2-b254-e56f662ac84c',
    nz: '0190e366-fbec-776f-8cb2-08a4fc55e311',
    br: '0190e36a-7658-7ffa-89b5-8fd8d2a5958e',
    mi: '0190e376-b8d2-79ce-86ad-b5c0d5d25bdf',
    pe: '0190e378-9c5c-7d78-8ae7-38240c480bbb',
    cl: '0190e37a-760c-79d5-aa1a-269932e73b42',
    co: '0190e37d-1ff3-7172-8592-7b79c4ff929b',
    at: '63148719-cda6-491f-9737-d7a53edc5d42',
    nl: '1a5c451e-b7a2-4439-84cd-acaf579c9149',
    be: '8c070da0-9422-440c-94b3-6bb589844324',
    ch: '0190e39c-705a-7fb8-a00f-69ae5ba3ccac',
    au: 'f31ce79d-fdd9-491f-bdf5-2539c34a1fc5',
    ca: 'f0fa73fd-2424-40b3-8b9d-b6d0795a71ea',
    cn: '68c9a61c-5582-495f-b447-9518c6a0c5bf',
    de: '697b043e-f485-43da-a6a6-2734a4ec8b88',
    dk: '1dc736f6-747e-489a-b63d-99c3d579ae0d',
    es: 'da79cfbb-7673-4761-b5eb-c9fcb6a8f1fd',
    fi: '58c78875-00a1-468d-b690-2d7feea0df81',
    fr: '826d79e4-068f-4dbf-a5d0-25a2ca7a8316',
    gb: '009a5a44-7658-4e58-b93c-3920cfe31961',
    hu: '3cdd9702-b7c9-4078-8564-a975dab79ac3',
    in: '337ba5fc-3a4c-4d14-94b5-23235ce52259',
    it: '959211c2-d26d-4248-9be6-3653b636c5d9',
    mx: '62854f5a-0804-4a50-9b06-e57c424d5d04',
    my: '5fe7808e-9d34-4b79-a522-cf404f882753',
    pt: '0258463a-9827-4ae3-b982-d5c5108c1fbd',
    se: 'ccd5f5d6-dd0a-47da-98f0-06a2800f5199',
    th: '8d30ab41-5f11-426e-9382-d5bd1a5729af',
    us: '79356f9e-23dd-4222-b2de-b0c37f4d5a5b',
    usme: '0190e376-b8d2-79ce-86ad-b5c0d5d25bdf',
  },
};

/** loads onetrust css */
export function loadOneClickCSS() {
  return new Promise<boolean>(function (resolve, reject) {
    try {
      const styleEl = document.createElement('style');
      styleEl.innerHTML =
        '#onetrust-consent-sdk *:focus,#onetrust-consent-sdk *:active,#onetrust-pc-sdk .ot-tgl input:focus+.ot-switch .ot-switch-nob {outline: none !important;}';

      styleEl.onload = () => resolve(true);
      styleEl.onerror = () => resolve(false);
      document.head.appendChild(styleEl);
    } catch (e) {
      resolve(false);
    }
  });
}
